/**
 * @file まなぶ人ごとの本だなの設定 (分野の並び) の読み書き。
 *
 * @what: まなぶ人が変わるたびに並びを読み直し、save で上書きする。save は先に画面へ反映し
 *   (押した順に並びが変わる)、失敗したら読み込んでいた並びへ戻して error に残す。
 * @why: 並び替えはボタンを何度も押す操作なので、保存の往復を待ってから並びを変えると遅れて見える。
 * @failure: 読み込みの失敗は既定の並び (空) のまま本だなを出し、console に残す。保存の失敗は
 *   error に1行の文を入れ、本だなの区画が知らせる。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchShelfSettings, putShelfSettings } from "../api/shelfSettings";

export interface ShelfSettingsState {
  /** 分野 id の並び (空 = 既定)。 */
  subjectOrder: readonly string[];
  /** 読み込みが終わったか。終わる前は並び替えの操作を出さない。 */
  ready: boolean;
  /** 保存に失敗したときの、画面に出す1行 (無ければ null)。 */
  error: string | null;
  save: (order: readonly string[]) => Promise<void>;
}

const EMPTY: readonly string[] = [];

export function useShelfSettings(familyId: string, personId: string | null): ShelfSettingsState {
  const [subjectOrder, setSubjectOrder] = useState<readonly string[]>(EMPTY);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 保存に失敗したとき戻す先 (最後にサーバと一致した並び)
  const committed = useRef<readonly string[]>(EMPTY);

  useEffect(() => {
    setSubjectOrder(EMPTY);
    committed.current = EMPTY;
    setError(null);
    setReady(false);
    if (!personId) return;
    const controller = new AbortController();
    fetchShelfSettings(familyId, personId, controller.signal)
      .then((s) => {
        if (controller.signal.aborted) return;
        committed.current = s.subject_order ?? EMPTY;
        setSubjectOrder(committed.current);
        setReady(true);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load shelf settings", err);
        setReady(true);
      });
    return () => controller.abort();
  }, [familyId, personId]);

  const save = useCallback(
    async (order: readonly string[]) => {
      if (!personId) return;
      setSubjectOrder(order);
      setError(null);
      try {
        const saved = await putShelfSettings(familyId, personId, order);
        committed.current = saved.subject_order ?? order;
        setSubjectOrder(committed.current);
      } catch (err: unknown) {
        console.error("[study] failed to save shelf settings", err);
        setSubjectOrder(committed.current);
        setError("並びを保存できませんでした。時間を置いてもう一度お試しください。");
      }
    },
    [familyId, personId],
  );

  return { subjectOrder, ready, error, save };
}
